import type { PermissionKey } from '../../types/permissions'
import { listParam } from '../../hooks/useListState'

/**
 * L-NAV-5 — LES ONGLETS DE LA PAGE « HISTORIQUE » (`/historique`).
 *
 * Un onglet par ancien journal : `/calls`, `/recordings`, `/sms/logs`,
 * `/emails/logs` (ces chemins redirigent, cf. `routes/legacyHistoryRedirects`).
 *
 * ── UN DRAPEAU PAR ONGLET (L-NAV-9) ──────────────────────────────────────
 * Visibilité de l'onglet ET accès à son contenu lisent le MÊME drapeau — celui
 * que le serveur lit sur `GET /journals/<kind>` :
 *   appels → canAccessCalls · enregistrements → canAccessRecordings ·
 *   SMS → canAccessSmsLogs · e-mails → canAccessEmailLogs.
 * Avant : visibilité `canAccessCalls` (+ `canManagePermissions` pour SMS et
 * e-mails), contenu SMS / e-mails sous `requiredRole="ADMIN"`. La compat
 * (`access/legacyFallbacks`) fait que `canManagePermissions` vaut encore les
 * deux drapeaux SMS / e-mails : aucun ADMIN de prod ne perd ses onglets. Un
 * vieux signet `/sms/logs` d'agent aboutit, comme avant, sur le refus d'accès.
 */
export const HISTORY_TABS = ['calls', 'recordings', 'sms', 'emails'] as const
export type HistoryTab = (typeof HISTORY_TABS)[number]

/** Onglet par défaut : `/historique` seul = l'historique des appels. */
export const DEFAULT_HISTORY_TAB: HistoryTab = 'calls'

/**
 * Paramètres d'URL possédés par la page hôte. `tab` choisit l'onglet ;
 * `status` et `direction` sont déclarés ICI pour que le changement d'onglet
 * les efface en UNE écriture (piège `setSearchParams` qui ne met pas en
 * file) : leurs valeurs n'ont pas le même sens d'un journal à l'autre
 * (`NO_ANSWER` d'appel ≠ statut de SMS). Recherche et période (`q`,
 * `period`, `start`, `end`) sont communes aux 4 journaux : elles SUIVENT
 * l'onglet (chercher un numéro dans les appels puis dans les SMS).
 */
export const HISTORY_HUB_SCHEMA = {
  tab: listParam.oneOf<HistoryTab>(HISTORY_TABS, DEFAULT_HISTORY_TAB),
  status: listParam.string(),
  direction: listParam.string(),
}

/** Patch d'URL d'un changement d'onglet. */
export function tabChangePatch(tab: HistoryTab) {
  return { tab, status: '', direction: '' }
}

/** Le drapeau de chaque onglet — lu au menu (page), à la route (contenu) et au serveur. */
export const HISTORY_TAB_PERMISSION: Readonly<Record<HistoryTab, PermissionKey>> = {
  calls: 'canAccessCalls',
  recordings: 'canAccessRecordings',
  sms: 'canAccessSmsLogs',
  emails: 'canAccessEmailLogs',
}

/** Onglets VISIBLES : ceux dont le compte a le drapeau. */
export function visibleHistoryTabs(hasPermission: (permission: PermissionKey) => boolean): HistoryTab[] {
  return HISTORY_TABS.filter((tab) => hasPermission(HISTORY_TAB_PERMISSION[tab]))
}

/** URL canonique d'un onglet (pour les redirections et les retours). */
export function historyHref(tab: HistoryTab, search = ''): string {
  const params = new URLSearchParams(search)
  params.delete('tab')
  const rest = params.toString()
  return `/historique?tab=${tab}${rest ? `&${rest}` : ''}`
}
